import * as React from 'react'

import {
  Badge,
  Box,
  Button,
  type Colors as ColorType,
  Icon,
  type MediumIcon,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import classNames from 'classnames/bind'
import type { $Values } from 'utility-types'

import styles from './index.module.css'

const PanesSizes = {
  small: 'small',
  medium: 'medium',
  large: 'large',
}

export type Size = $Values<typeof PanesSizes>
type Colors =
  | 'primary'
  | 'radical'
  | 'terciary'
  | 'correct'
  | 'wrong'
  | 'important'
  | 'accent'
  | 'negative'
  | 'brand'
  | 'white'
type ActionIcons = 'settings' | 'pencil'

export interface PanesHeaderActionProps {
  onClick: () => void
  icon: ActionIcons
  description: string
}

export interface PanesHeaderInlineInfoProps {
  icon: MediumIcon
  message: string
}

export interface HeaderProps {
  size?: Size
  illustration?: React.ComponentProps<typeof Icon.Large>['icon']
  title: string
  description?: string | string[] | React.ReactNode
  descriptionLink?: React.ReactNode
  type?: Colors
  centered?: boolean
  fill?: boolean
  separation?: boolean
  line?: boolean
  counter?: number
  action?: PanesHeaderActionProps | null | undefined
  inlineInfo?: PanesHeaderInlineInfoProps[]
}

export interface TitleProps {
  value: string
  counter?: number
  size?: Size
  color?: ColorType
}

export interface DescriptionProps {
  value: string[] | string | React.ReactNode
}

const cx = classNames.bind(styles)

function Title({ value, counter, size = PanesSizes.medium, color }: TitleProps) {
  let TextComponent = Text.Header2

  if (size === 'large') TextComponent = Text.Header1

  if (size === 'small') TextComponent = Text.Header3

  return (
    <div className={styles.title}>
      <TextComponent color={color} weight="semibold">
        {value}
      </TextComponent>
      {!!(counter && counter > 0) && <Badge>{counter}</Badge>}
    </div>
  )
}

function Description({ value }: DescriptionProps) {
  const description = Array.isArray(value) ? value : [value]

  return (
    <div className={styles.description}>
      {description.map((d, i) => (
        <Text.Normal color="grey800" key={i}>
          {d}
        </Text.Normal>
      ))}
    </div>
  )
}

function Action({ icon, description, onClick }: PanesHeaderActionProps) {
  const [open, toggle] = useToggle(false)

  return (
    <div className={styles.action}>
      <Tooltip.Alt
        attachment="top center"
        targetAttachment="bottom center"
        open={open}
        toggle={toggle}
        label={description}
      >
        <Button onClick={onClick} type="secondary" iconName={icon} />
      </Tooltip.Alt>
    </div>
  )
}

function PanesHeader({
  illustration,
  title,
  description,
  descriptionLink,
  counter,
  line = false,
  size = PanesSizes.medium,
  separation = true,
  action,
  inlineInfo,
}: HeaderProps) {
  return (
    <div
      className={cx('header', size, {
        separation,
      })}
    >
      <Box justifyContent="spaceBetween" flexDirection="row">
        {illustration && (
          <div className={styles.illustration}>
            <Icon.Large icon={illustration} color="radicalRed1000" />
          </div>
        )}
        {action && (
          <Action icon={action.icon} description={action.description} onClick={action.onClick} />
        )}
      </Box>
      <Title size={size} value={title} counter={counter} color="grey1000" />
      {description && <Description value={description} />}
      {descriptionLink && (
        <Box alignItems="flexStart" flexDirection="column" gap="s24" paddingTop="s8">
          {descriptionLink}
        </Box>
      )}
      {line && <div className={cx('line', 'radical')} />}
      {inlineInfo && (
        <Box alignItems="flexStart" flexDirection="column" gap="s24" paddingTop="s48">
          {inlineInfo.map((tag: PanesHeaderInlineInfoProps, i) => (
            <Box key={i} flexDirection="row" alignItems="center" justifyContent="center" gap="s12">
              <Icon.Medium icon={tag.icon} color="grey800" />
              <Text.Normal color="grey800" size="200" weight="medium">
                {tag.message}
              </Text.Normal>
            </Box>
          ))}
        </Box>
      )}
    </div>
  )
}

export default PanesHeader
